import Card from "../../../components/Card"
import { Column, Row } from "../../../components/Layout"
import TextBanner from "../../../components/typography/TextBanner"
import { JobListing, JobListingStatus, JobsSummaryDocument } from "../../../types"
import AdvertsSummary from "./AdvertsSummary"
import JobsTable, { JobTableFilterData, JobsTableEntry, JobsTableFilters, initialJobTableFilterData } from "./JobsTable"
import { Key, useMemo, useState } from "react"
import TextLabel from "../../../components/typography/TextLabel"

import { v4 as uuidv4 } from 'uuid';
import useJobsPageStore from "../hooks/useJobsPageStore"
import TextWithRegion from "../../../components/typography/TextWithRegion"
import { Box } from "@mui/material"
interface CompanySummaryProps {
    summary: JobsSummaryDocument | null
    loading: boolean
}

const dummyTableEntry: JobsTableEntry = {
    title: "-",
    category: "-",
    url: "-",
    location: "-",
    status: "Open"
}

const dummyCount = 10
const dummyEntries: JobsTableEntry[] = []

for (let i = 0; i < dummyCount; i++) {
    dummyEntries.push({ ...dummyTableEntry, url: uuidv4() })
}

export default function CompanyJobsListings({ summary, loading }: CompanySummaryProps) {

    const { selectedCompanyName } = useJobsPageStore()
    const noDataFound = (summary == null) && !loading
    const dataFound = ((summary != null) && !loading)

    const [filterData, setFilterData] = useState<JobTableFilterData>(initialJobTableFilterData)
    const { jobTitleQuery, selectedStatuses, selectedLocations, selectedCategories } = filterData


    function areSameUrl(a: string, b: string): boolean {
        try {
            const urlA = new URL(a);
            const urlB = new URL(b);

            // Check protocol, host, and normalized pathname
            const pathnameA = urlA.pathname.replace(/\/$/, '');
            const pathnameB = urlB.pathname.replace(/\/$/, '');

            if (
                urlA.protocol !== urlB.protocol ||
                urlA.host !== urlB.host ||
                pathnameA !== pathnameB
            ) {
                return false;
            }

            // Compare query parameters as sorted key-value pairs
            const paramsA = new URLSearchParams(urlA.search);
            const paramsB = new URLSearchParams(urlB.search);

            const entriesA = Array.from(paramsA.entries()).sort();
            const entriesB = Array.from(paramsB.entries()).sort();

            if (entriesA.length !== entriesB.length) return false;

            for (let i = 0; i < entriesA.length; i++) {
                const [keyA, valueA] = entriesA[i];
                const [keyB, valueB] = entriesB[i];
                if (keyA !== keyB || valueA !== valueB) return false;
            }

            return true;

        } catch {
            // Not valid URLs — fall back to direct string comparison
            return a === b;
        }
    }

    const processedJobs = useMemo(
        () => {
            return (dataFound) ? (() => {
                const key = (job: JobListing) => `${job.url}::${job.location ?? 'null'}`;
                const newJobKeys = new Set(summary.advertsNew.map(key));

                const merged: JobsTableEntry[] = [
                    ...summary.advertsNew.map(job => ({ ...job, status: "New" as JobListingStatus })),
                    ...summary.advertsClosed.map(job => ({ ...job, status: "Closed" as JobListingStatus })),
                    ...summary.adverts
                        .filter(job => !newJobKeys.has(key(job)))
                        .map(job => ({ ...job, status: "Open" as JobListingStatus })),
                ]
                return merged
            })() : []
        },
        [summary?.advertsNew, summary?.advertsClosed, summary?.adverts]
    )

    const entries = useMemo(
        () => {
            if (loading) { return dummyEntries }


            return processedJobs.filter(job => {

                const jobTitleMatch = job.title.toLowerCase().includes(jobTitleQuery.toLowerCase()) || areSameUrl(jobTitleQuery.toLowerCase(), job.url.toLowerCase())

                if (selectedStatuses.length && !selectedStatuses.includes(job.status)) return false;
                if (jobTitleQuery && !jobTitleMatch) return false;
                if (selectedLocations.length && (!job.location || !selectedLocations.includes(job.location))) return false;
                if (selectedCategories.length && (!job.category || !selectedCategories.includes(job.category))) return false;
                return true;
            });
        },
        [processedJobs, filterData, loading]
    )

    const uniqueLocations: string[] = useMemo(
        () => {
            return (dataFound) ? Array.from(new Set(summary.adverts.map(a => a.location).filter(Boolean) as string[])) : []
        },
        [summary?.adverts]
    )

    const uniqueCategories: string[] = useMemo(
        () => {
            if (dataFound) {
                return Array.from(new Set(summary.adverts.map(a => a.category).filter(Boolean) as string[]))
            }
            return []
        },
        [summary?.adverts]
    );

    const advertsTotalCount = dataFound ? entries.filter(e => e.status != "Closed").length : 0
    const advertsTotalNew = dataFound ? entries.filter(e => e.status == "New").length : 0
    const advertsTotalClosed = dataFound ? entries.filter(e => e.status == "Closed").length : 0
    const lastUpdated = dataFound ? new Date(summary.scanResultsLatest.timestamp) : new Date()
    const filtersDisabled = entries.length == 0

    return (
        <Card className="h-full" fullWidth>

            <Column className="h-full" fullWidth>

                <Box width={'100%'}>
                    <Row>
                        <Column className="w-4/12" mx={2}>
                            <TextLabel>Company Name</TextLabel>
                            <TextBanner>
                                <TextWithRegion text={selectedCompanyName ?? ""} showCountryIcon />
                            </TextBanner>
                        </Column>
                        <Column className="w-8/12">
                            <AdvertsSummary
                                loading={loading}
                                total={advertsTotalCount}
                                new={advertsTotalNew}
                                closed={advertsTotalClosed}
                                lastUpdated={lastUpdated}
                            />
                        </Column>
                    </Row>

                    <JobsTableFilters
                        loading={loading}
                        disabled={filtersDisabled}
                        onFiltersChanged={setFilterData}
                        categoriesOptions={uniqueCategories}
                        locationsOptions={uniqueLocations}
                        filterResetDeps={[summary]}
                    />

                </Box>

                <Box flexGrow={1} width={'100%'}>
                    <JobsTable loading={loading} entries={entries} />
                </Box>

            </Column>

        </Card>
    )

}